import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Link2, RefreshCw } from 'lucide-react';
import type { DeskDetail } from '@erp/shared';
import { api } from '@/lib/api-client';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/misc';
import { errorMessage } from '@/features/desks/desks-queries';

/**
 * Formulário externo da mesa: link público (/form/<token>) para abertura de
 * tickets sem login. Gerar/rotacionar/desativar são rotas próprias — o token
 * nunca passa pelo PATCH da mesa.
 *
 * Só admin vê a seção (v5 review): o token é a credencial do endpoint público —
 * a API o mascara (null) e responde 403 às mutations para os demais papéis,
 * então renderizar aqui só mostraria uma seção quebrada.
 */
export function DeskFormSection({ desk }: { desk: DeskDetail }) {
  const user = useAuthUser();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const token = desk.externalFormToken;
  const link = token !== null ? `${window.location.origin}/form/${token}` : null;
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(t);
  }, [copied]);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['erp', 'desks'] });

  const generate = useMutation({
    mutationFn: async () => {
      const res = await api<unknown>(`/desks/${desk.id}/form-token`, { method: 'POST' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const disable = useMutation({
    mutationFn: async () => {
      const res = await api<void>(`/desks/${desk.id}/form-token`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  // depois de todos os hooks (rules of hooks)
  if (!isAdmin) return null;

  const copyLink = async () => {
    if (!link) return;
    setCopyError(false);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      setCopyError(true);
    }
  };

  const busy = generate.isPending || disable.isPending;

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">Formulário externo</h2>
        {token !== null ? <Badge variant="success">Ativo</Badge> : <Badge>Inativo</Badge>}
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Link público para qualquer pessoa abrir tickets nesta mesa sem login.
      </p>

      {token === null ? (
        <Button size="sm" disabled={busy} onClick={() => generate.mutate()}>
          <Link2 className="size-4" />
          {generate.isPending ? 'Gerando…' : 'Gerar link'}
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Input
              readOnly
              value={link ?? ''}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Link do formulário externo"
              className="h-8 flex-1 font-mono text-xs"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void copyLink()}
              title="Copiar link"
            >
              {copied ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
              {copied ? 'Copiado' : 'Copiar'}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                if (
                  window.confirm(
                    'Gerar um novo link? O link atual deixará de funcionar imediatamente.'
                  )
                ) {
                  generate.mutate();
                }
              }}
            >
              <RefreshCw className="size-3.5" />
              {generate.isPending ? 'Rotacionando…' : 'Rotacionar'}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={busy}
              className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
              onClick={() => {
                if (
                  window.confirm('Desativar o formulário externo? O link deixará de funcionar.')
                ) {
                  disable.mutate();
                }
              }}
            >
              {disable.isPending ? 'Desativando…' : 'Desativar'}
            </Button>
          </div>
        </div>
      )}

      {copyError ? (
        <p className="mt-2 text-sm text-destructive">
          Não foi possível copiar — selecione o link e copie manualmente.
        </p>
      ) : null}
      {(generate.error ?? disable.error) ? (
        <p className="mt-2 text-sm text-destructive">
          {errorMessage(generate.error ?? disable.error)}
        </p>
      ) : null}
    </section>
  );
}
